// Pure data + lookup helpers for the character creator: hero archetypes,
// palette chips for the pack's named colour variants, and the preview crop.
// No DOM here on purpose — this module is unit-tested in node.
import { CHAR_SLOTS, type CharSlot, type CharStyle } from './manifest.generated';
import type { Appearance } from './compositor';

/** Where the character actually lives inside the 64×64 player frame.
 *  Measured over every creator layer (idle + walk rows): pixels span
 *  x 22..42, y 17..41 — so a 64×64 canvas is ~90% empty air. The preview
 *  crops to this window instead, which is what makes the hero read big. */
export const PREVIEW_CROP = { x: 20, y: 15, w: 24, h: 28 } as const;

export interface Swatch { label: string; hex: string }

/** Skin tints. The skin layer is multiplied (see compositor), so these are
 *  "how much light passes" values — white keeps the sheet's own tone. */
export const SKIN_TONES: Swatch[] = [
  { label: 'Porcelain', hex: '#ffffff' },
  { label: 'Fair', hex: '#f2ddc8' },
  { label: 'Warm', hex: '#e3c1a0' },
  { label: 'Olive', hex: '#c9a077' },
  { label: 'Tan', hex: '#b07f57' },
  { label: 'Bronze', hex: '#94643f' },
  { label: 'Umber', hex: '#6f4527' },
  { label: 'Ebony', hex: '#4c2d19' },
];

/** Chip colour for a pack preset label (Farmer_Shirt "Blue" etc.). */
const LABEL_HEX: Record<string, string> = {
  black: '#2c2c33', blue: '#4a76c8', brown: '#8a5a3c', green: '#5c9a4a',
  orange: '#d98032', pink: '#e08bb5', purple: '#8a5fbf', red: '#c0453c',
  white: '#efe7d8', 'white and brown': '#c9a67c', grey: '#9a9a9a', gray: '#9a9a9a',
  blonde: '#e0c169', ginger: '#c1642c', default: '#c9a67c',
};

/** Colour chip for a preset label — tolerant of the pack's messy labels
 *  ("t 1 Red", "1 Black", "White and Brown"). */
export function swatchForLabel(label: string): string {
  const l = label.toLowerCase().trim();
  if (LABEL_HEX[l]) return LABEL_HEX[l];
  // Longest key that appears in the label wins ("white and brown" over "white").
  const keys = Object.keys(LABEL_HEX).sort((a, b) => b.length - a.length);
  for (const k of keys) if (l.includes(k)) return LABEL_HEX[k];
  return '#c9a67c';
}

/** The same palette as LABEL_HEX, as a named list. A wardrobe that shows only
 *  coloured squares is unreadable to a colour-blind player — every chip and the
 *  custom picker have to be able to SAY which colour they are, so the swatch
 *  grid needs a name for an arbitrary hex, not just for the pack's labels. */
export const NAMED_COLOURS: Swatch[] = [
  { label: 'Black', hex: '#2c2c33' }, { label: 'Grey', hex: '#9a9a9a' },
  { label: 'White', hex: '#efe7d8' }, { label: 'Brown', hex: '#8a5a3c' },
  { label: 'Tan', hex: '#c9a67c' }, { label: 'Red', hex: '#c0453c' },
  { label: 'Orange', hex: '#d98032' }, { label: 'Blonde', hex: '#e0c169' },
  { label: 'Green', hex: '#5c9a4a' }, { label: 'Blue', hex: '#4a76c8' },
  { label: 'Purple', hex: '#8a5fbf' }, { label: 'Pink', hex: '#e08bb5' },
];

/** Plain-English name for any hex — "roughly which crayon is this".
 *  Matched in HSL, not RGB: plain Euclidean RGB distance called a pale green
 *  (#7fe07f) "Grey", because grey sits in the middle of the cube and is close
 *  to everything. Hue decides the name whenever there is any chroma at all. */
export function colourName(hex: string): string {
  const [h, s, l] = hexToHsl(hex);
  let best = NAMED_COLOURS[0], bestD = Infinity;
  for (const c of NAMED_COLOURS) {
    const [ch, cs, cl] = hexToHsl(c.hex);
    const dh = Math.min(Math.abs(h - ch), 1 - Math.abs(h - ch));
    // A neutral name only competes for a neutral sample, and vice versa. Without
    // that hard split, Grey/White/Black sit near the middle of every axis and win
    // against a real hue — a pale green came out as "Grey".
    const coloured = s > 0.2, cColoured = cs > 0.2;
    const neutralGap = coloured === cColoured ? 0 : 2;
    const d = (coloured && cColoured ? dh * 4 : 0) + neutralGap + Math.abs(s - cs) * 0.5 + Math.abs(l - cl);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best.label;
}

/** The pack's preset labels carry slicing debris ("t 1 Red", "1 Black").
 *  Chips now show their name as text, so the debris became visible copy. */
export function prettyLabel(label: string): string {
  const words = label.trim().split(/\s+/).filter((t) => /^[A-Za-z]{2,}$/.test(t));
  return words.length ? words.join(' ') : label.trim();
}

export const slotById: Record<string, CharSlot> =
  Object.fromEntries(CHAR_SLOTS.map((s) => [s.id, s]));

/** The style family a file belongs to (falls back to the slot's first style). */
export function styleOfFile(slotId: string, file: string | null | undefined): CharStyle {
  const slot = slotById[slotId];
  return slot.styles.find((st) => st.presets.some((p) => p.file === file)) ?? slot.styles[0];
}

/** Resolve `slot / style / first matching label` to a layer file.
 *  Any miss degrades gracefully instead of throwing, so a re-sliced pack
 *  that drops a colour can never break the creator. */
export function fileFor(slotId: string, styleId: string | null, labelPrefs: string[] = []): string | null {
  const slot = slotById[slotId];
  if (!slot) return null;
  const style = (styleId && slot.styles.find((s) => s.id === styleId)) || slot.styles[0];
  if (!style?.presets.length) return null;
  for (const pref of labelPrefs) {
    const m = style.presets.find((p) => p.label.toLowerCase().includes(pref.toLowerCase()));
    if (m) return m.file;
  }
  return style.presets[0].file;
}

export interface HeroPreset {
  id: string;
  label: string;
  blurb: string;
  skin: string;              // multiply tint hex
  /** slotId → [styleId | null, label preferences]; a missing slot = empty. */
  slots: Record<string, [string | null, string[]]>;
}

/** Distinct hero looks. The pack ships a single body sheet, so an
 *  "archetype" is a full head-to-toe outfit + skin tone, not a second base. */
export const HERO_PRESETS: HeroPreset[] = [
  {
    id: 'farmer', label: 'Farmhand', blurb: 'Straw hat, honest work',
    skin: '#f2ddc8',
    slots: {
      pants: ['Farmer_Pants', ['White and Brown', 'Brown']],
      shoes: ['shoes', ['Brown']],
      shirt: ['Farmer_Shirt', ['Green']],
      hair: ['Hair_1', ['Brown']],
      headgear: ['farmer_hat', ['Default']],
    },
  },
  {
    id: 'lumberjack', label: 'Woodcutter', blurb: 'Flannel and forest air',
    skin: '#e3c1a0',
    slots: {
      pants: ['OG_Pants', ['Brown', 'Black']],
      shoes: ['shoes', ['Black']],
      shirt: ['Lumberjack_Shirt', ['Red']],
      hair: ['Hair_3', ['Black']],
      headgear: [null, []],
    },
  },
  {
    id: 'noble', label: 'Noble', blurb: 'Silk, and a taste for gold',
    skin: '#ffffff',
    slots: {
      pants: ['Royal_Pants', ['Purple', 'Black']],
      shoes: ['shoes', ['Purple', 'Black']],
      shirt: ['Royal_Shirt', ['Purple']],
      hair: ['Hair_5', ['Blonde']],
      headgear: [null, []],
    },
  },
  {
    id: 'wanderer', label: 'Wanderer', blurb: 'Road dust, no fixed address',
    skin: '#b07f57',
    slots: {
      pants: ['OG_Pants', ['Green']],
      shoes: ['shoes', ['Brown']],
      shirt: ['OG_Shirt', ['Blue']],
      hair: ['Hair_4', ['Ginger']],
      headgear: [null, []],
    },
  },
  {
    id: 'herbalist', label: 'Herbalist', blurb: 'Shroom-picker of the glade',
    skin: '#94643f',
    slots: {
      pants: ['Farmer_Pants', ['Purple']],
      shoes: ['shoes', ['Green']],
      shirt: ['Farmer_Shirt', ['Pink']],
      hair: ['Hair_6', ['Grey']],
      headgear: ['farmer_hat', ['Default']],
    },
  },
  {
    id: 'ranger', label: 'Ranger', blurb: 'Quiet steps, sharp eyes',
    skin: '#6f4527',
    slots: {
      pants: ['OG_Pants', ['Black']],
      shoes: ['shoes', ['Black']],
      shirt: ['Lumberjack_Shirt', ['Green']],
      hair: ['Hair_2', ['Black']],
      headgear: [null, []],
    },
  },
];

/** Turn a hero archetype into a full Appearance the compositor can draw. */
export function buildHeroAppearance(preset: HeroPreset): Appearance {
  const a: Appearance = {
    skin: { file: fileFor('skin', null, []), color: preset.skin === '#ffffff' ? null : preset.skin },
  };
  for (const slot of CHAR_SLOTS) {
    if (slot.id === 'skin') continue;
    const spec = preset.slots[slot.id];
    if (!spec || spec[0] === null) { a[slot.id] = { file: null, color: null }; continue; }
    a[slot.id] = { file: fileFor(slot.id, spec[0], spec[1]), color: null };
  }
  return a;
}

/** Which archetype (if any) the current appearance still matches — used to
 *  keep the hero cards in sync while the player tweaks individual slots. */
export function matchHeroPreset(app: Appearance): string | null {
  for (const p of HERO_PRESETS) {
    const built = buildHeroAppearance(p);
    const same = Object.keys(built).every((k) => (app[k]?.file ?? null) === (built[k].file ?? null));
    if (same) return p.id;
  }
  return null;
}

/** hex → [hue 0..1, sat 0..1, lightness 0..1]. */
function hexToHsl(hex: string): [number, number, number] {
  const [r0, g0, b0] = hexToRgb(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r0, g0, b0), min = Math.min(r0, g0, b0), d = max - min;
  const l = (max + min) / 2;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r0) h = ((g0 - b0) / d) % 6;
  else if (max === g0) h = (b0 - r0) / d + 2;
  else h = (r0 - g0) / d + 4;
  h /= 6;
  return [h < 0 ? h + 1 : h, s, l];
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Closest swatch in a palette to an arbitrary hex (marks the active chip
 *  when the player has dragged the custom colour picker). */
export function nearestSwatch(hex: string, palette: Swatch[]): Swatch {
  const [r, g, b] = hexToRgb(hex);
  let best = palette[0], bestD = Infinity;
  for (const s of palette) {
    const [sr, sg, sb] = hexToRgb(s.hex);
    const d = (r - sr) ** 2 + (g - sg) ** 2 + (b - sb) ** 2;
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}
